import MeetingCard from "../../components/MeetingCard/Index";
import { useServerTime } from "../../hooks/useServerTime";
import { Container, FullList, FullListWrapper } from "./Style";
import { useFetch } from '../../hooks/useFetch';
import { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import Subtitle from "../../components/Subtitle";
import SearchField from "../../components/SearchField/Index";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { Avatar, Typography, Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const MAX_RECOMMENDED = 6;

// Fisher-Yates numa copia: o array vem do cache do SWR e nao pode ser mexido
const embaralhar = lista => {
    const copia = [...lista];

    for (let i = copia.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copia[i], copia[j]] = [copia[j], copia[i]];
    }

    return copia;
};

const FindMeetings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Sincroniza o relogio antes de decidir quais horarios ja passaram
    useServerTime();

    const navItems = [
        { label: 'Ao vivo', path: '/lista/ao-vivo' },
        { label: 'Meus encontros', path: '/encontros' },
        { label: 'Acompanhando', path: '/acompanhando' },
        ...(Boolean(user?.profile)
            ? [{ label: 'Encontros criados', path: '/meus-encontros' }]
            : []
        ),
        { label: 'Calendário', path: '/calendario' },
    ];
    const [searchInput, setSearchInput] = useState('');
    const [filterData, setFilterData] = useState({});
    const [filters, setFilters] = useState({});
    const [queryParams, setQueryParams] = useSearchParams();
    const { data, pagination, meta } = useFetch('/search', { ...Object.fromEntries(queryParams), limit: 6 });
    const userProfileId = meta?.userProfileId;
    const { data: hostsData } = useFetch('/hosts');
    // Ordem sorteada a cada montagem da tela, para nao privilegiar sempre os
    // mesmos anfitrioes do inicio da lista
    const hostsEmbaralhados = useMemo(() => (hostsData ? embaralhar(hostsData) : []), [hostsData]);
    const { data: recommendedData } = useFetch('/search/recommended');
    const hasSearchParams = Array.from(queryParams.entries())
        .filter(([key, value]) => {
            if (['params', 'page', 'limit'].includes(key)) return false;
            if (value === '' || value === 'false' || value === 'null' || value === 'undefined') return false;
            return true;
        })
        .length > 0;

    const hasEnoughRecommended = recommendedData?.length >= 5;
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        setFilterData(data);
    }, [data]);

    const Search = () => {
        const tmp = {};

        // Texto de busca
        if (searchInput) {
            tmp.title = searchInput;
            tmp.seller_name = searchInput;
        }

        // Data/hora combinados. Sem new Date(): 'aaaa-mm-dd' seria lido como UTC
        // e cairia no dia anterior. So manda a hora se a pessoa digitou uma.
        if (filters.datetime) {
            const [dataISO = '', hora = ''] = String(filters.datetime).split('T');
            if (dataISO) tmp.date = dataISO;
            if (hora) tmp.time = hora;
        }

        // Modalidade
        if (filters.raffle !== undefined) tmp.raffle = filters.raffle;

        // Visibilidade
        if (filters.visible_public !== undefined) tmp.visible_public = filters.visible_public;

        // Range de preco
        if (filters.price_range) {
            if (filters.price_range[0] > 0) tmp.price_min = filters.price_range[0];
            if (filters.price_range[1] < 500) tmp.price_max = filters.price_range[1];
        }

        // Topicos
        if (filters.topics?.length > 0) tmp.topics = filters.topics.join(',');

        const cleanedParams = {
            params: '',
            ...Object.fromEntries(
                Object.entries(tmp).filter(([_, value]) =>
                    value !== undefined && value !== null && value !== ''
                )
            )
        };

        setQueryParams(cleanedParams);
        setShowAll(true); // Entra automaticamente na aba "Todos os encontros"
    };

    return (
        <Container>
            {/* Anfitrioes */}
            {hostsEmbaralhados.length > 0 && (
                <Box sx={{ width: '90%', '@media (max-width: 750px)': { width: '100%' } }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5, display: 'block' }}>
                        Anfitriões
                    </Typography>
                    <Swiper
                        modules={[FreeMode]}
                        slidesPerView="auto"
                        freeMode
                        spaceBetween={6}
                        style={{ padding: '6px 2px' }}
                    >
                        {hostsEmbaralhados.map((host) => (
                            <SwiperSlide key={host.id} style={{ width: 'auto' }}>
                                <Box
                                    onClick={() => navigate(`/anfitriao/${host.username}`)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        width: 64,
                                        '&:hover .host-avatar': { borderColor: '#006a61' },
                                    }}
                                >
                                    <Avatar
                                        className="host-avatar"
                                        src={host.profileImage}
                                        alt={host.name}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            border: '2px solid #e0e0e0',
                                            transition: 'border-color 0.2s',
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mt: 0.3,
                                            fontSize: '0.6rem',
                                            color: 'text.secondary',
                                            textAlign: 'center',
                                            lineHeight: 1.2,
                                            maxWidth: 64,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        @{host.username}
                                    </Typography>
                                </Box>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            )}

            {/* Busca */}
            <SearchField
                inputValue={searchInput}
                inputChange={setSearchInput}
                search={Search}
                filters={filters}
                setFilters={setFilters}
                onClear={() => { setQueryParams({}); setShowAll(false); }}
            />

            {/* Resultado da busca */}
            {hasSearchParams && (
                <FullListWrapper>
                    <Subtitle text="Resultado da pesquisa" />
                    <FullList>
                        {filterData?.meetings?.length === 0
                            ? <Typography variant="body2" color="text.secondary">Nenhum encontro encontrado.</Typography>
                            : filterData?.meetings?.map((meeting) => (
                                <MeetingCard
                                    key={meeting.id}
                                    id={meeting.id}
                                    picture={meeting.Profile.User.profileImage}
                                    title={meeting.title}
                                    user={meeting.Profile.User.username}
                                    description={meeting.description}
                                    raffle={meeting.raffle}
                                    privacy={meeting.visible_public}
                                    free={meeting.free}
                                    pricing={meeting.pricing}
                                    dates={meeting.meeting_schedule}
                                    isOwner={userProfileId && meeting.profile_id === userProfileId}
                                    topics={meeting.topics}
                                />
                            ))
                        }
                    </FullList>
                    {pagination && <Pagination pagination={pagination} />}
                </FullListWrapper>
            )}

            {/* Recomendados */}
            {!hasSearchParams && !showAll && hasEnoughRecommended && (
                <FullListWrapper>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Subtitle text="Recomendados para você" />
                        <Typography
                            variant="body2"
                            onClick={() => setShowAll(true)}
                            sx={{
                                color: '#006a61',
                                cursor: 'pointer',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Ver todos
                        </Typography>
                    </Box>
                    <FullList>
                        {recommendedData.slice(0, MAX_RECOMMENDED).map((meeting) => (
                            <MeetingCard
                                key={meeting.id}
                                id={meeting.id}
                                picture={meeting.Profile.User.profileImage}
                                title={meeting.title}
                                user={meeting.Profile.User.username}
                                description={meeting.description}
                                raffle={meeting.raffle}
                                privacy={meeting.visible_public}
                                free={meeting.free}
                                pricing={meeting.pricing}
                                dates={meeting.meeting_schedule}
                                isOwner={userProfileId && meeting.profile_id === userProfileId}
                            />
                        ))}
                    </FullList>
                </FullListWrapper>
            )}

            {/* Todos os encontros */}
            {!hasSearchParams && (showAll || !hasEnoughRecommended) && (
                <FullListWrapper>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Subtitle text="Todos os encontros" />
                        {hasEnoughRecommended && (
                            <Typography
                                variant="body2"
                                onClick={() => setShowAll(false)}
                                sx={{
                                    color: '#006a61',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Recomendados
                            </Typography>
                        )}
                    </Box>
                    <FullList>
                        {filterData?.meetings?.length === 0
                            ? <Typography variant="body2" color="text.secondary">Nenhum encontro disponível.</Typography>
                            : filterData?.meetings?.map((meeting) => (
                                <MeetingCard
                                    key={meeting.id}
                                    id={meeting.id}
                                    picture={meeting.Profile.User.profileImage}
                                    title={meeting.title}
                                    user={meeting.Profile.User.username}
                                    description={meeting.description}
                                    raffle={meeting.raffle}
                                    privacy={meeting.visible_public}
                                    free={meeting.free}
                                    pricing={meeting.pricing}
                                    dates={meeting.meeting_schedule}
                                    isOwner={userProfileId && meeting.profile_id === userProfileId}
                                    topics={meeting.topics}
                                />
                            ))
                        }
                    </FullList>
                    {pagination && <Pagination pagination={pagination} />}
                </FullListWrapper>
            )}
        </Container>
    );
}

export default FindMeetings;
